import "../styles/recommended-person.css"

import { Button, Dropdown, MenuProps, Modal, notification } from "antd"
import { MenuItemGroupType } from "antd/es/menu/interface"
import dayjs from "dayjs"
import {
  BadgeHelp,
  BriefcaseBusiness,
  ChevronDownIcon,
  CircleCheckBig,
  Copy,
  Link,
  Mail,
  MapPin,
  Minus,
  Phone,
  Plus,
  VerifiedIcon
} from "lucide-react"
import { lazy, memo, Suspense, useEffect, useState } from "react"

import { syncAccountWithSalesforce } from "~/api/accounts"
import { addToYourPeopleList } from "~/api/people"
import { getEmailEnrichment } from "~/api/priorities"
import linkedinLogo from "~/assets/linkedin.png"
import syncIcon from "~/assets/sync-icon.png"
import GeneratePersonBriefButton from "~/containers/person-brief"
import { useCopyToClipboard } from "~/hooks/useClipboard"
import useGlobalStore from "~/store/global"
import { IRecommendedPerson } from "~/types/priority"
import { track } from "~/utils/amplitude"
import { EVENTS, getBaseEventProps } from "~/utils/amplitude-events"
import { sanitizeUrl, toCapitalize } from "~/utils/helpers"
import { getLocationDisplay, getLocationKey } from "~/utils/location-helpers"

const SyncLeadWithSalesforce = lazy(
  () => import("~/containers/account/sync-lead-with-salesforce")
)

import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { removeChampion } from "~/api/champions-tracking"
import CallNowButton, {
  useShowGetPhone
} from "~/containers/people-os/call-now-button"
import GetMobileNumber from "~/components/mobile-number"
import { AddToPeopleListButton } from "~/containers/people-list/add-to-people-list"
import { EmailTemplateType } from "~/types/accounts"

import Loader, { OSLoader } from "./base-loader"
import CustomTooltip from "./custom-tooltip"

const PersonOrgChart = lazy(
  () => import("~/containers/org-chart/person-org-chart")
)

const ASCENDION_TENANT_ID = 74

const employementChangeTypes: any = {
  TITLE: "label_recently_changed_role",
  ORGANIZATION: "label_recently_joined_organization",
  ADD_ORGANIZATION: "label_recently_added_organization"
} as const

const employementChangeTypeColors: any = {
  TITLE: "bg-[#fdecce]",
  ORGANIZATION: "bg-[#e2defa]",
  ADD_ORGANIZATION: "bg-[#0bc2f5]"
} as const

export default function RecommendedPerson({
  person,
  className = "",
  hideEmail = false,
  hidePhone = false,
  hideMessageTemplate = false,
  hideKnowYourProspect = false,
  hideAddToTeam = false,
  addOrRemovePersonToOpp,
  hideEmployementChange = true,
  onClickMessageTemplate,
  isAddedToOpp,
  showCompany,
  opp_id,
  yourAddedPeopleSection,
  hideAddPersonToYourPeopleList,
  isSalesforceEnabled,
  dataTestid,
  hide_org_chart
}: {
  person: IRecommendedPerson
  className?: string
  hideEmail?: boolean
  hidePhone?: boolean
  hideMessageTemplate?: boolean
  hideKnowYourProspect?: boolean
  hideAddToTeam?: boolean
  hideEmployementChange?: boolean
  onClickMessageTemplate?: (templateType: EmailTemplateType) => void
  addOrRemovePersonToOpp?: (
    person: IRecommendedPerson,
    action: "add" | "remove"
  ) => Promise<void>
  isAddedToOpp?: boolean
  showCompany?: boolean
  opp_id?: number
  yourAddedPeopleSection?: boolean
  hideAddPersonToYourPeopleList?: boolean
  isSalesforceEnabled?: boolean
  dataTestid?: string
  hide_org_chart?: boolean
}) {
  const { t } = useTranslation()
  const showGetPhone = useShowGetPhone()
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const user = useGlobalStore((store) => store.user)
  const tenant = useGlobalStore((store) => store.tenant)
  const tenantPref = tenant?.preferences
  const featureFlags = user?.feature_flags
  const isSalesforceLeadEnabled =
    featureFlags?.is_sf_enabled && tenantPref?.sfdc_leadsync
  const isSfAdminEnabled =
    tenantPref?.is_sf_admin_enabled && user?.role === "tenant_admin"

  const isSfEnabledBlocked = !!tenantPref?.is_sf_enabled_blocked
  const [isSyncing, setIsSyncing] = useState(person?.sfdc_stamp == "INITIATED")
  const [notificationApi, contextHolder] = notification.useNotification()
  const [stamp, setStamp] = useState(person?.sfdc_stamp)
  const [addingPersonToOpp, setAddingPersonToOpp] = useState(false)

  const employementChangeType = employementChangeTypes[
    person.last_employment_change_type
  ]
    ? t(employementChangeTypes[person.last_employment_change_type])
    : t(employementChangeTypes.ORGANIZATION)

  const employementChangeTypeColor =
    employementChangeTypeColors[person.last_employment_change_type] ??
    employementChangeTypeColors.ORGANIZATION

  const changedDuration = Math.ceil(
    dayjs().diff(dayjs(person.last_employment_changed_at), "month", true)
  )

  if (!person) return

  const finalStamp = person?.sfdc_stamp || stamp || ""

  return (
    <>
      {contextHolder}
      <div
        key={person.id}
        className={`dark:bg-dark-2 bg-white p-4 rounded-2xl border-[0.5px] border-light-border flex flex-col gap-2 shadow-lg ${className} relative`}
        data-testid={dataTestid}
      >
        <div className="flex gap-2 items-center justify-between">
          <div className="flex gap-2 items-center">
            <a
              href={sanitizeUrl(person?.linkedin_url)}
              target="_blank"
              rel="noreferrer"
              className="flex gap-2 items-center hover:underline"
              onClick={() =>
                track(EVENTS.COMMON.LINKEDIN_OPENED, {
                  ...getBaseEventProps({
                    feature: "people",
                    accountId: person?.account_id,
                    accountName: getLocationKey(person?.account_name)
                  }),
                  name: getLocationKey(person.name),
                  id: person.id
                })
              }
              data-testid="recommended-person-link"
            >
              <img
                src={linkedinLogo}
                alt="linkedin"
                width={16}
                height={16}
                className="w-4 h-4 min-w-4 min-h-4"
              />
              <p
                className="text-13 font-semibold leading-[21px] dark:text-dark-5 text-dark-2"
                data-testid="recommended-person-name"
              >
                {getLocationDisplay(person.name)}
                {showCompany &&
                  ` | ${getLocationDisplay(person.short_name) || person.short_name || person.account_name}`}
              </p>
            </a>
            <CallNowButton
              phone={(person as any).phone}
              person={{
                id: person.id,
                name: getLocationKey(person.name),
                linkedin_url: person.linkedin_url,
                job_title: getLocationKey(person.job_title),
                account_name: getLocationKey(person.account_name),
                location:
                  getLocationKey(person.location_name) ||
                  getLocationKey(person.location_country) ||
                  getLocationKey(person.location_region)
              }}
            />
          </div>
          <div className="flex gap-2 items-center">
            {isSalesforceLeadEnabled && (
              <Suspense fallback={null}>
                <SyncLeadWithSalesforce
                  tooltipText="Sync this lead to Salesforce"
                  person_id={person.id}
                />
              </Suspense>
            )}
            {person?.org_people_id && !hide_org_chart && (
              <Suspense fallback={null}>
                <PersonOrgChart person={person} />
              </Suspense>
            )}
            {!hideAddToTeam && (
              <CustomTooltip
                title={
                  <p className="text-xs">
                    {isAddedToOpp
                      ? t("label_remove_this_person_from_your_saved_contacts")
                      : t("label_add_this_person_to_your_saved_contacts")}
                  </p>
                }
              >
                <Button
                  className={`bg-transparent rounded-lg border-[0.5px] dark:border-primary-light border-primary p-2 h-6 ${
                    isAddedToOpp ? "!border-red-400" : ""
                  }`}
                  onClick={async () => {
                    track(EVENTS.COMMON.ADD_PERSON_TO_OPP_CLICKED, {
                      ...getBaseEventProps({
                        accountId: person.account_id ?? undefined
                      }),
                      action: isAddedToOpp ? "remove" : "add",
                      person_id: person.id
                    })
                    setAddingPersonToOpp(true)
                    try {
                      const promises = []
                      if (!isAddedToOpp) {
                        const payload = person.id
                          ? {
                              person_id: person.id,
                              account_id: person.account_id ?? null
                            }
                          : person.linkedin_url
                            ? {
                                linkedin_urls: [person.linkedin_url],
                                account_id: person.account_id ?? null
                              }
                            : null

                        if (payload && tokenInfo && !person?.champion_id) {
                          promises.push(
                            addToYourPeopleList(
                              tokenInfo,
                              payload,
                              linkedTenantId
                            )
                          )
                        }
                      } else {
                        promises.push(
                          removeChampion(
                            tokenInfo as any,
                            person.champion_id ?? person.id,
                            linkedTenantId
                          )
                        )
                      }
                      promises.push(
                        addOrRemovePersonToOpp?.(
                          person,
                          isAddedToOpp ? "remove" : "add"
                        )
                      )
                      await Promise.allSettled(promises)
                      if (
                        opp_id !== undefined &&
                        user &&
                        isSalesforceEnabled &&
                        !isSfAdminEnabled &&
                        !isSfEnabledBlocked
                      ) {
                        setIsSyncing(true)

                        const isAscendion =
                          (linkedTenantId || tokenInfo?.tenant_id) ===
                          ASCENDION_TENANT_ID

                        const { data } = await syncAccountWithSalesforce(
                          tokenInfo!,
                          {
                            tenant_id: tokenInfo?.tenant_id as number,
                            user_id: user.id,
                            account_id: person.account_id?.toString() ?? "",
                            type: "CONTACT",
                            current_user_id: tokenInfo?.user_id as number,
                            opportunity_ids: [opp_id],
                            people_id: person.id
                          }
                        )
                        const response = data?.data?.response

                        if (isAscendion) {
                          try {
                            await syncAccountWithSalesforce(tokenInfo!, {
                              tenant_id: tokenInfo?.tenant_id as number,
                              user_id: user.id,
                              account_id: person.account_id?.toString() ?? "",
                              type: isAddedToOpp
                                ? "CONTACT_OPP_UNLINK"
                                : "CONTACT_OPP_LINK",
                              current_user_id: tokenInfo?.user_id as number,
                              opportunity_ids: [opp_id],
                              people_id: person.id
                            })
                          } catch {
                            // best-effort, not surfaced to the user
                          }
                        }

                        if (
                          typeof response === "string" &&
                          response.trim() !== ""
                        ) {
                          Modal.success({
                            title: response.trim(),
                            centered: true
                          })
                        }
                        setStamp(response)
                        setIsSyncing(false)
                      }

                      setAddingPersonToOpp(false)
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (error) {
                      notificationApi.error({
                        message: t("label_failed_to_save_person"),
                        placement: "topRight"
                      })
                      setAddingPersonToOpp(false)
                    }
                  }}
                  disabled={addingPersonToOpp}
                  loading={addingPersonToOpp}
                  data-testid={
                    isAddedToOpp
                      ? "remove-person-to-priority"
                      : "add-person-to-priority"
                  }
                >
                  {isAddedToOpp ? (
                    <Minus size={12} strokeWidth={3} className="text-red-500" />
                  ) : (
                    <Plus
                      size={12}
                      strokeWidth={3}
                      className="text-primary dark:text-primary-light"
                    />
                  )}
                </Button>
              </CustomTooltip>
            )}
            {!hideAddPersonToYourPeopleList && (
              <div className="absolute top-2 right-2">
                <AddToPeopleListButton
                  person={{
                    linkedin_url: person.linkedin_url,
                    account_id: person.account_id,
                    champion_id: person?.champion_id,
                    name: person.name,
                    account_name: person.account_name,
                    job_title: person.job_title
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {person.job_title && (
          <div
            className="flex gap-2 items-start w-fit"
            data-testid="job-title-recommended-person"
          >
            <BriefcaseBusiness
              size={16}
              className="dark:!text-gray-300 text-dark-2 w-4 h-4 min-w-4 min-h-4"
            />
            <p className="text-xs leading-[18px] dark:text-text-gray-300 text-dark-2">
              {getLocationDisplay(person.job_title)}
            </p>
          </div>
        )}

        {(person.location_name ||
          person.location_country ||
          person.location_region ||
          person.location) && (
          <div
            className="flex gap-2 items-start w-fit"
            data-testid="location-recommended-person"
          >
            <MapPin
              size={16}
              className="dark:!text-gray-300 text-dark-2 w-4 h-4 min-w-4 min-h-4"
            />
            <p className="text-xs leading-[18px] dark:text-text-gray-300 text-dark-2">
              {getLocationDisplay(person.location_name) ||
                getLocationDisplay(person.location_country) ||
                getLocationDisplay(person.location_region) ||
                person.location}
            </p>
          </div>
        )}
        {!hideEmail && (
          <GetEmailButton
            linkedin_url={person.linkedin_url}
            initialEmail={
              (person as any).type === "crm_deal_contact"
                ? person.email
                : undefined
            }
          />
        )}
        {!hidePhone && (person as any).phone ? (
          <div className="flex gap-2 items-center w-fit text-xs">
            <Phone
              size={14}
              className="dark:!text-gray-300 !text-dark-2 w-4 h-4 min-w-4 min-h-4"
            />
            <span className="text-primary text-left text-xs dark:text-primary-light">
              {(person as any).phone}
            </span>
          </div>
        ) : !hidePhone && showGetPhone ? (
          <GetMobileNumber
            person={
              {
                ...person,
                name: getLocationKey(person.name),
                linkedin_url: person.linkedin_url,
                seniority: getLocationKey(person.job_title),
                account: getLocationKey(person.account_name),
                location:
                  getLocationKey(person.location_name) ||
                  getLocationKey(person.location_country) ||
                  getLocationKey(person.location_region) ||
                  person.location
              } as any
            }
            className="flex gap-2 items-center w-fit justify-start text-xs p-0 h-fit text-primary dark:hover:!text-dark-6 dark:text-primary-light"
            canAddNewNumber={false}
            icon={
              <Phone
                size={14}
                className="dark:!text-gray-300 !text-dark-2 w-4 h-4 min-w-4 min-h-4"
              />
            }
          >
            <span className="text-primary text-left text-xs dark:text-primary-light">
              {t("label_get_phone")}
            </span>
          </GetMobileNumber>
        ) : null}

        {isSalesforceEnabled &&
          (person?.sfdc_stamp === "NOT_FOUND" ||
          !yourAddedPeopleSection ? null : isSyncing && !finalStamp ? (
            <div className="flex items-start gap-2 w-fit">
              <OSLoader className="xs" />
              <span className="text-xs italic text-dark-2 dark:text-dark-6">
                {t("components.label_syncing_with_crm")}
              </span>
            </div>
          ) : finalStamp?.trim() !== "" ? (
            <div
              className="flex text-xs leading-[18px] dark:text-text-gray-300 text-dark-2 gap-2"
              data-testid="sync-stamp"
            >
              <img
                src={syncIcon}
                width={16}
                alt="Sync"
                className="w-4 h-4 min-w-4 min-h-4"
              />
              <p>{finalStamp}</p>
            </div>
          ) : null)}

        {!hideEmployementChange && (
          <div className="flex flex-end gap-2 flex-wrap">
            {person.seniority && (
              <p
                className={`px-2 py-1 rounded-2xl text-xs center bg-[#ccf1ea] text-dark-2 border-[0.5px] border-[#00b894]`}
                data-testid="seniority-recommended-person"
              >
                {(() => {
                  const seniorityKey = getLocationKey(person.seniority)
                  const seniorityDisplay = getLocationDisplay(person.seniority)
                  return seniorityKey === "c_suite"
                    ? t("account_summary.label_seniority_cxo")
                    : seniorityKey === "vp"
                      ? t("account_summary.label_seniority_svp_vp")
                      : toCapitalize(seniorityDisplay || seniorityKey)
                })()}
              </p>
            )}
            <p
              className={`px-2 py-1 rounded-2xl text-xs center text-dark-2 ${employementChangeTypeColor} border-[0.5px] border-light-border`}
              data-testid="employment-change-type-recommended-person"
            >
              {employementChangeType}
            </p>
            <p
              className={`px-2 py-1 rounded-2xl text-xs center text-dark-2 bg-[#cefdfa] border-[0.5px] border-[#0bc2f5]`}
              data-testid="employment-change-duration-recommended-person"
            >
              {`${t("label_last")} ${changedDuration} ${changedDuration > 1 ? t("label_months") : t("label_month")}`}
            </p>
          </div>
        )}
        <div className="flex gap-2 flex-1 items-end flex-wrap justify-start">
          {!hideMessageTemplate && (
            <Dropdown
              placement="bottomLeft"
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "LINKEDIN",
                    "data-testid": "linkedin-message",
                    icon: (
                      <img
                        src={linkedinLogo}
                        alt="Linkedin icon"
                        width={12}
                        height={12}
                      />
                    ),
                    label: (
                      <span className="text-xs">
                        {t("label_linkedin_message")}
                      </span>
                    ),
                    onClick: () => onClickMessageTemplate?.("LINKEDIN")
                  },
                  {
                    key: "EMAIL",
                    "data-testid": "email-message",
                    icon: <Mail size={14} strokeWidth={1.5} />,
                    label: (
                      <span className="text-xs">
                        {t("label_email_message")}
                      </span>
                    ),
                    onClick: () => onClickMessageTemplate?.("EMAIL")
                  }
                ]
              }}
            >
              <button
                className="dark:text-dark-6 text-dark-2 p-2 border-[0.5px] border-light-border rounded-lg text-xs dark:bg-dark-3 bg-white flex items-center gap-1"
                data-testid="generate-message-recommended-person"
              >
                <span>{t("label_generate_message")}</span>{" "}
                <ChevronDownIcon size={14} />
              </button>
            </Dropdown>
          )}
          {!hideKnowYourProspect &&
            !person.linkedin_url?.includes("dummy-") && (
              <GeneratePersonBriefButton
                accountId={person.account_id}
                personName={getLocationDisplay(person.name)}
                account_name={getLocationDisplay(person.account_name)}
                linkedin_url={person.linkedin_url}
              />
            )}
        </div>

        {person?.circles ? (
          <Circles
            data={person?.circles}
            className="border-[0.5px] text-xs leading-4 rounded-2xl px-2 py-1 max-w-[50%] absolute -top-4 right-4 content-center text-center cursor-pointer hover:bg-gray-100 hover:text-primary dark:hover:bg-dark-3 dark:hover:text-primary dark:text-dark-1 dark:bg-dark-3 flex items-center justify-center gap-1"
          />
        ) : (
          ""
        )}
      </div>
    </>
  )
}

const CircleListItem = ({
  name,
  linkedinUrl = "",
  designation,
  showHr = true
}: {
  name: string
  linkedinUrl: string
  designation: string
  showHr: boolean
}) => {
  return (
    <a href={sanitizeUrl(linkedinUrl)} target="_blank" rel="noreferrer">
      <>
        <div className="p-2 px-2 flex">
          <img
            src={linkedinLogo}
            alt="linkedin"
            className="w-4  h-4 mt-1"
          ></img>
          <div className="flex-col px-2 ">
            <p className="text-sm font-semibold ">{name}</p>
            <p className="text-xs text-gray-500">{designation}</p>
          </div>
        </div>
        {showHr && <hr className="center mx-2 dark:opacity-50"></hr>}
      </>
    </a>
  )
}
export const Circles = ({
  data,
  className,
  placement = "topLeft"
}: {
  data: Record<string, any>
  className: string
  placement?:
    | "topLeft"
    | "bottom"
    | "topCenter"
    | "topRight"
    | "bottomLeft"
    | "bottomCenter"
    | "bottomRight"
    | "top"
}) => {
  const user = useGlobalStore((store) => store.user)
  const myLinkedInUrl = useGlobalStore((store) => store.userLinkedinUrl)
  const isMyLinkedInPresent = data.some(
    (person: any) => person.connect_linkedin_url === myLinkedInUrl
  )

  const displayText = () => {
    if (data.length === 0) return ""
    if (data.length === 1) {
      return isMyLinkedInPresent ? (
        <span>
          <span className="font-bold">You</span> are a first-degree connection
        </span>
      ) : (
        <span>
          <span className="font-bold">{data[0].connect_name}</span> from
          <span> {user?.tenant_name} is a first degree connection</span>
        </span>
      )
    }
    const othersCount = data.length
    if (isMyLinkedInPresent) {
      return (
        <span>
          <span className="font-bold">You + {othersCount - 1}</span> others are
          first-degree connections
        </span>
      )
    } else {
      return (
        <span>
          <span className="font-bold">{othersCount}</span> from
          <span>
            {" "}
            {user?.tenant_name} are first degree
            {othersCount === 1 ? " connection" : " connections"}
          </span>
        </span>
      )
    }
  }

  const childMenuItems =
    data &&
    data.map((person: any, index: number) => {
      if (person.connect_linkedin_url === myLinkedInUrl) return
      const showHr =
        isMyLinkedInPresent && data.length > 1
          ? index !== data.length - 2
          : index !== data.length - 1
      const dataItem = (
        <CircleListItem
          key={person.connect_linkedin_url}
          name={person?.connect_name}
          designation={person?.connect_designation}
          linkedinUrl={person?.connect_linkedin_url}
          showHr={showHr}
        />
      )
      const keyItem = `${person.connect_linkedin_url}-${index}`
      return {
        key: keyItem,
        label: dataItem
      }
    })
  const groupTitle: MenuItemGroupType = {
    key: "heading_for_all",
    type: "group",
    label: (
      <div
        style={{ backgroundColor: "rgb(226, 243, 251)" }}
        className=" dark:bg-dark-2 text-dark-2 p-2"
      >
        <span className="p-2 px-2 flex">{displayText()}</span>
      </div>
    ),
    children: [...childMenuItems]
  }
  const items: MenuProps["items"] = [groupTitle]

  return (
    <div>
      <Dropdown
        menu={{ items }}
        trigger={["hover"]}
        className=""
        overlayClassName="max-w-[320px] circle-menu"
        placement={placement}
      >
        <button
          className={className}
          style={{
            backgroundColor: "#E6F7FF",
            color: "#2D2D2D",
            borderColor: "#BAE7FF"
          }}
        >
          <span className="items-start flex flex-row gap-1">
            <div className="text-[10px] ">
              <Link size={14} className="rotate-45" />
            </div>
            <span className="line-clamp-1 items-start text-left">
              {displayText()}
            </span>
          </span>
        </button>
      </Dropdown>
    </div>
  )
}

export const GetEmailButton = memo(
  ({
    personId,
    accountId,
    className = "",
    linkedin_url,
    emailClassname = "",
    iconOnly = false,
    initialEmail,
    autoFetch = false,
    isExclusion = false,
    exclusionReason = null
  }: {
    personId?: number
    accountId?: number
    className?: string
    linkedin_url?: string
    emailClassname?: string
    iconOnly?: boolean
    initialEmail?: string | null
    autoFetch?: boolean
    isExclusion?: boolean
    exclusionReason?: string | null
  }) => {
    const { t } = useTranslation()
    const [notificationApi, notificationContextHolder] =
      notification.useNotification()
    const { priority_id: priorityId, id: account_id } = useParams()
    const tokenInfo = useGlobalStore((store) => store.tokenInfo)
    const [emailResponse, setEmailResponse] = useState<any>(
      initialEmail ? { email: initialEmail } : null
    )
    const [copiedText, copy] = useCopyToClipboard()
    const onClickGetEmail = async () => {
      if (emailResponse?.email === "NA") return
      if (emailResponse?.email) {
        copy(emailResponse.email)
        track(EVENTS.COMMON.VIEW_EMAIL_ID, {
          ...getBaseEventProps({
            feature: "people",
            accountId: accountId?.toString() || account_id
          }),
          id: personId,
          success: !!(emailResponse.email && emailResponse.email !== "NA"),
          isNew: true
        })
        return
      }
      setEmailResponse((s: any) => ({ ...s, loading: true }))
      const response = await getEmailEnrichment({
        tokenInfo: tokenInfo!,
        accountId: (accountId || account_id! || "").toString(),
        personId: personId!,
        opportunityId: priorityId!,
        linkedin_url
      })
      if (response.status === 200) {
        setEmailResponse((s: any) => ({ ...s, ...response.data.data }))
      } else {
        notificationApi.error({
          message: t("label_error"),
          description: t("label_something_went_wrong")
        })
      }
      setEmailResponse((s: any) => ({ ...s, loading: false }))
    }
    useEffect(() => {
      if (autoFetch && !emailResponse?.email && !emailResponse?.loading) {
        onClickGetEmail()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFetch])
    return (
      <>
        {notificationContextHolder}
        <CustomTooltip
          title={
            isExclusion && exclusionReason ? (
              <p className="text-xs">{exclusionReason}</p>
            ) : emailResponse?.email && emailResponse?.email !== "NA" ? (
              <p className="text-xs">
                {emailResponse?.is_verified
                  ? t("components.label_email_verified")
                  : t("components.label_email_not_verified")}
              </p>
            ) : iconOnly ? (
              <p className="text-xs">{t("label_get_email")}</p>
            ) : null
          }
        >
          <button
            type="button"
            aria-label={
              iconOnly
                ? emailResponse?.email &&
                  emailResponse?.email !== "NA" &&
                  !emailResponse?.loading
                  ? String(emailResponse.email)
                  : t("label_get_email")
                : undefined
            }
            className={`flex gap-2 items-center w-fit justify-start ${
              iconOnly && !emailResponse?.email && !emailResponse?.loading
                ? "justify-center gap-1"
                : ""
            } ${className}`}
            onClick={onClickGetEmail}
            disabled={emailResponse?.email === "NA"}
            data-testid="get-email-recommended-person"
          >
            <Mail
              size={14}
              className="dark:!text-gray-300 text-dark-2 w-4 h-4 min-w-4 min-h-4"
            />
            {emailResponse?.loading ? (
              iconOnly ? (
                <Loader style={{ fontSize: 12 }} />
              ) : (
                <div className="flex gap-2 items-center w-full">
                  <p className="text-xs dark:text-text-gray-300 text-dark-2">
                    {t("components.label_fetching_email")}
                  </p>
                  <Loader style={{ fontSize: 12 }} />
                </div>
              )
            ) : !iconOnly || emailResponse?.email ? (
              <p
                className={`text-primary text-left text-xs dark:text-primary-light ${
                  emailResponse?.email && emailResponse?.email !== "NA"
                    ? "!text-primary dark:!text-primary-light"
                    : ""
                } ${
                  iconOnly && emailResponse?.email
                    ? "line-clamp-1 max-w-[120px]"
                    : ""
                } ${emailClassname} ${isExclusion && emailResponse?.email ? "blur-sm" : ""}`}
              >
                {emailResponse?.email || t("label_get_email")}
              </p>
            ) : null}
            {!isExclusion &&
              emailResponse?.email &&
              (emailResponse?.is_verified ? (
                <VerifiedIcon
                  size={16}
                  className="text-green-500 w-4 h-4 min-w-4 min-h-4"
                />
              ) : emailResponse?.email === "NA" ? null : (
                <BadgeHelp
                  size={16}
                  className="text-orange-500 w-4 h-4 min-w-4 min-h-4"
                />
              ))}
            {!isExclusion &&
              emailResponse?.email &&
              emailResponse?.email !== "NA" &&
              (copiedText ? (
                <CircleCheckBig size={16} className="text-green-500" />
              ) : (
                <Copy
                  size={16}
                  className={
                    "w-4 h-4 min-w-4 min-h-4 dark:text-white text-dark-2"
                  }
                />
              ))}
          </button>
        </CustomTooltip>
      </>
    )
  }
)
GetEmailButton.displayName = "GetEmailButton"
